import { Check, ChevronDown, Globe } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { trackLanguageSwitch } from "#/lib/analytics";
import { LOCALE_META, LOCALES, useI18n } from "#/lib/i18n";

/**
 * Language dropdown (AR / FR / EN). Switching updates the active locale, which
 * re-renders all copy and flips `dir`/`lang` on <html>, and persists a cookie
 * so the choice survives reloads (read back during SSR).
 */
export function LanguageSwitcher({
	tone = "light",
	className = "",
}: {
	tone?: "light" | "dark";
	className?: string;
}) {
	const { locale, setLocale } = useI18n();
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const dark = tone === "dark";

	useEffect(() => {
		if (!open) return;
		function onClick(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") setOpen(false);
		}
		document.addEventListener("mousedown", onClick);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onClick);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	const current = LOCALE_META[locale];

	return (
		<div ref={ref} className={`relative ${className}`}>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={current.label}
				className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
					dark
						? "bg-white/10 text-white ring-1 ring-white/15 hover:bg-white/15"
						: "bg-white text-navy ring-1 ring-line hover:bg-teal-50"
				}`}
			>
				<Globe className="h-4 w-4 opacity-70" />
				<span className="min-w-[2.4rem] text-start">{current.label}</span>
				<ChevronDown
					className={`h-3.5 w-3.5 opacity-60 transition-transform duration-200 ${
						open ? "rotate-180" : ""
					}`}
				/>
			</button>

			{open && (
				<div className="absolute end-0 z-50 mt-2 w-44 origin-top overflow-hidden rounded-2xl bg-white p-1.5 shadow-lift ring-1 ring-line animate-fade-up">
					{LOCALES.map((code) => {
						const meta = LOCALE_META[code];
						const active = code === locale;
						return (
							<button
								key={code}
								type="button"
								aria-current={active}
								dir={meta.dir}
								onClick={() => {
									if (code !== locale) trackLanguageSwitch(locale, code);
									setLocale(code);
									setOpen(false);
								}}
								className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-semibold transition-colors ${
									active
										? "bg-teal-50 text-teal-600"
										: "text-navy hover:bg-canvas"
								}`}
							>
								<span className="flex items-center gap-2.5">
									<span className="text-base leading-none">{meta.flag}</span>
									{meta.label}
								</span>
								{active && <Check className="h-4 w-4 flex-none" />}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}
